import { graphql } from 'gatsby';
import { Button } from 'hem-ui';
import get from 'lodash/get';
import React, { Suspense, useEffect } from 'react';

import {
  ProductList,
  sendGtmProductClickEvent,
  sendGtmProductImpressionsEvent,
} from '@/utils/sendGtmEvents';

import { ArticlePreview } from '../ArticlePreview';
import { Poster } from '../Poster';
import { fixSlug } from '../RichText/RichText';
import RichTextInstagram from '../RichText/RichTextInstagram';
import { RichTextReview } from '../RichText/RichTextReview';
import { SectionTitle } from '../SectionTitle';
import { VariantGridItem } from '../VariantGrid';
import {
  articleWrapper,
  linkWrapper,
  posterWrapper,
  root,
} from './Grid.module.scss';

export const Grid: React.FC<Queries.GridFragment> = ({
  title,
  content,
  entries,
  link,
}): JSX.Element => {
  useEffect(() => {
    const hits = entries?.filter(
      (hit) => hit.__typename === 'ContentfulProductVariant',
    ) as unknown as Queries.VariantGridItemFragment[];

    if (hits && hits.length > 0) {
      sendGtmProductImpressionsEvent({
        list: ProductList.EditorialPage,
        impressions: hits
          .map((hit, index) => {
            if (hit?.shopify?.price === undefined) {
              return null;
            }

            return {
              sku: hit.sku,
              productName: hit.productName,
              variantName: hit.variantName,
              price: hit.shopify.price.toString(),
              originalPrice: (
                hit.shopify.compareAtPrice || hit.shopify.price
              )?.toString(),
              position: index,
              slug: hit.slug,
            };
          })
          .filter(Boolean),
      });
    }
  }, [entries]);

  return (
    <>
      {!!title && (
        <SectionTitle
          titleTag="h3"
          title={title}
          markdown={content?.childMarkdownRemark?.rawMarkdownBody}
          border
        />
      )}
      <div className={root}>
        {entries?.map((entry, index) => {
          switch (entry.__typename) {
            case 'ContentfulComponentPoster': {
              const isLandScape =
                get(entry, 'image.gatsbyImageData.width', 0) >
                get(entry, 'image.gatsbyImageData.height', 0);

              return (
                <div
                  key={entry.contentful_id}
                  className={posterWrapper}
                  style={{ gridRow: isLandScape ? 'span 1' : 'span 2' }}
                  data-grid-column={entry.columnSpan}
                >
                  <Suspense>
                    <Poster {...entry} isLandScape={isLandScape} />
                  </Suspense>
                </div>
              );
            }
            case 'ContentfulPageArticle':
              return (
                <div key={entry.slug} className={articleWrapper}>
                  <ArticlePreview {...entry} />
                </div>
              );
            case 'ContentfulProductVariant':
              if (!entry.shopify || !entry.airtable) {
                return null;
              }
              return (
                <VariantGridItem
                  key={entry.sku}
                  {...entry}
                  onClick={() => {
                    sendGtmProductClickEvent({
                      sku: entry.sku,
                      productName: entry.productName,
                      variantName: entry.variantName,
                      price: entry.shopify.price + '',
                      originalPrice:
                        (entry.shopify.compareAtPrice || entry.shopify.price) +
                        '',
                      slug: entry.slug,
                      list: ProductList.EditorialPage,
                      position: index,
                    });
                  }}
                />
              );
            case 'ContentfulInstagram':
              return <RichTextInstagram key={entry.photo.id} {...entry} />;
            case 'ContentfulYotpoProductReview':
              return (
                <RichTextReview key={entry.title} {...entry} placement="grid" />
              );
            default:
              console.log('Grid entry not rendered', { entry });
              return null;
          }
        })}
      </div>
      {!!link && (
        <div className={linkWrapper}>
          <Button to={fixSlug(link.entry?.slug || link.slug)}>
            {link.title}
          </Button>
        </div>
      )}
    </>
  );
};

export default Grid;

export const query = graphql`
  fragment Grid on ContentfulComponentGrid {
    __typename
    contentful_id
    title
    content {
      childMarkdownRemark {
        rawMarkdownBody
      }
    }
    entries {
      __typename
      ... on ContentfulComponentPoster {
        ...Poster
      }
      ... on ContentfulProductVariant {
        ...VariantGridItem
      }
      ... on ContentfulInstagram {
        ...RichTextInstagram
      }
      ... on ContentfulPageArticle {
        ...ArticlePreview
      }
      ... on ContentfulYotpoProductReview {
        ...ProductReview
      }
    }
    link {
      ...Link
    }
  }
`;
